/** The account's trading wallet, permissions and orders (arbitrage custody, docs/API.md "Trading wallets and permissions"). */
import { queryOptions, useQuery } from '@tanstack/react-query'
import type { OrderState } from '@algoboo/schema/api'
import { request } from '../client'
import { qk } from '../keys'

/** GET /wallet: the account's one trading wallet (lane BOOCLEAN1); polls while it is still being derived. */
export function useAccountWallet(enabled = true) {
  return useQuery({
    queryKey: qk.accountWallet(),
    queryFn: ({ signal }) => request('getWallet', { signal }),
    enabled,
    refetchInterval: (q) => (q.state.data?.wallet?.status === 'requested' ? 1500 : false),
  })
}

export const portfolioQuery = () =>
  queryOptions({
    queryKey: qk.portfolio(),
    queryFn: ({ signal }) => request('getMyPortfolio', { signal }),
    refetchInterval: (q) => (q.state.data?.wallet?.wallet.status === 'requested' ? 1500 : 30_000),
  })

/** GET /me/portfolio: the account's wallet with balances, lots and permissions; polls while it is derived. */
export function usePortfolio(enabled = true) {
  return useQuery({ ...portfolioQuery(), enabled })
}

export const permissionOptionsQuery = () => queryOptions({ queryKey: qk.permissionOptions(), queryFn: ({ signal }) => request('getPermissionOptions', { signal }), staleTime: 60_000 })

export function usePermissionOptions(enabled = true) {
  return useQuery({ ...permissionOptionsQuery(), enabled })
}

export const walletStatementQuery = () => queryOptions({ queryKey: qk.walletStatement(), queryFn: ({ signal }) => request('getWalletStatement', { signal, query: {} }) })

/** GET /wallet/statement: the account's wallet's deposits, withdrawals, fills, fees and gas. */
export function useWalletStatement(enabled = true) {
  return useQuery({ ...walletStatementQuery(), enabled })
}

export const ordersQuery = (wallet: string | null, state?: OrderState) =>
  queryOptions({
    queryKey: [...qk.orders(wallet), state ?? null],
    queryFn: ({ signal }) => request('listOrders', { signal, query: { ...(wallet ? { wallet } : {}), ...(state ? { state } : {}), limit: 50 } }),
  })

export function useOrders(wallet: string | null, state?: OrderState, enabled = true) {
  return useQuery({ ...ordersQuery(wallet, state), enabled })
}
